import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Footer } from "@/components/ui/footer";
import { Navbar } from "@/components/ui/navbar";
import { Check, Mail, AlertCircle } from "lucide-react";
import { unsubscribeEmail } from "@/server/unsubscribe";
import { z } from "zod";

const unsubscribeSearchSchema = z.object({
  email: z.string().email().optional().catch(undefined),
  listId: z.string().uuid().optional().catch(undefined),
  token: z.string().optional().catch(undefined),
  listName: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: unsubscribeSearchSchema,
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { email, listId, token, listName } = Route.useSearch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnsubscribed, setIsUnsubscribed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);

  const handleUnsubscribe = async () => {
    if (!email) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await unsubscribeEmail({
        data: { email, listId, token, listName },
      });

      if ("redirect" in result && result.redirect) {
        setRedirectUrl(result.redirect);
        setIsUnsubscribed(true);
      } else if (result.success) {
        setIsUnsubscribed(true);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error("Error unsubscribing:", err);
      setError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // No email provided
  if (!email) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-4">
              Invalid Unsubscribe Link
            </h1>
            <p className="text-slate-600">
              This unsubscribe link is missing an email address. Please use the
              link from your email to unsubscribe.
            </p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Successfully unsubscribed
  if (isUnsubscribed) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-6 py-20">
          <div className="max-w-md text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-green-600" />
            </div>
            {redirectUrl ? (
              <>
                <h1 className="text-3xl font-bold text-slate-900 mb-4">
                  Manage Your Follows
                </h1>
                <p className="text-slate-600 mb-6">
                  You're following this list with your Wayra account. You can
                  manage your follows from your dashboard.
                </p>
                <Link
                  to={redirectUrl ?? '/dashboard/lists'}
                  className="inline-block bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-3 text-base font-medium"
                >
                  Go to Dashboard
                </Link>
              </>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-slate-900 mb-4">
                  You've Been Unsubscribed
                </h1>
                <p className="text-slate-600 mb-2">
                  <span className="font-medium text-slate-900">{email}</span>{" "}
                  has been unsubscribed from{" "}
                  {listName ? (
                    <span className="font-medium text-slate-900">
                      {listName}
                    </span>
                  ) : (
                    "marketing emails"
                  )}
                  .
                </p>
                {listName ? (
                  <p className="text-slate-500 text-sm">
                    You'll still receive digest emails from other lists you're
                    subscribed to.
                  </p>
                ) : (
                  <p className="text-slate-500 text-sm">
                    You won't receive any promotional emails from us. You may
                    still receive transactional emails like receipts or account
                    updates.
                  </p>
                )}
              </>
            )}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Unsubscribe form
  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 py-20">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-8 h-8 text-slate-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">
            Unsubscribe
          </h1>
          <p className="text-slate-600 mb-6">
            {listName
              ? `Are you sure you want to unsubscribe from "${listName}"?`
              : "Are you sure you want to unsubscribe the following email address from our mailing list?"}
          </p>

          <div className="bg-slate-50 rounded-xl px-6 py-4 mb-6">
            <p className="font-medium text-slate-900">{email}</p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 rounded-xl px-6 py-4 mb-6 text-sm">
              {error}
            </div>
          )}

          <Button
            onClick={handleUnsubscribe}
            disabled={isSubmitting}
            className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Unsubscribing..." : "Unsubscribe"}
          </Button>

          <p className="text-slate-500 text-sm mt-6">
            Changed your mind? Just close this page.
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
